import { Component, computed, DestroyRef, inject, input, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { RelatedResourceCardComponentInput } from 'src/app/components/sidepanel-cards/related-resource-card/related-resource-card.component';
import { RelatedViolationCardComponentInput } from 'src/app/components/sidepanel-cards/related-violation-card/related-violation-card.component';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { ITimelineItem } from 'src/app/components/timeline/timeline.component';
import {
  InventoryEntityFieldStatus,
  InventoryResourceModel,
  ResourceCategory,
} from 'src/app/models/api/inventoryResource.model';
import { MenuAction } from 'src/app/models/menuAction.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { QueryParametersModel } from 'src/app/models/viewModels/tableRowViewModel';
import { InventoryActionsService } from 'src/app/services/actions/inventory/inventory-actions.service';
import { InventorySupportedActions } from 'src/app/services/actions/inventory/inventorySupportedActions';
import { IInventoryResourceViewModel } from 'src/app/models/viewModels/inventoryResourceViewModel';
import { populateErrorMessage } from '../utils/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterNavigationParams } from '../../components/anchor-button/anchor-link.component';
import { InventoryResourceService } from './inventory-resource.service';
import { getResourceCategoryDisplayName } from 'src/app/common/categoryUtils';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { ICardField } from 'src/app/models/viewModels/cardModel';
import { RefreshMethod } from 'src/app/models/api/inventoryResource.model';

@Component({
  selector: 'app-inventory-resource',
  templateUrl: './inventory-resource.component.html',
  styleUrls: ['./inventory-resource.component.scss'],
  providers: [InventoryResourceService],
})
export class InventoryResourceComponent implements OnInit {
  private readonly _inventoryResourceService = inject(InventoryResourceService);
  private readonly _inventoryActionsService = inject(InventoryActionsService);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _beautifyDisplayStringPipe = inject(BeautifyDisplayStringPipe);

  public integration = input.required<string>();
  public resource = input.required<string>();
  public viewModel = input<IInventoryResourceViewModel>();

  public InventoryEntityFieldStatusEnum = InventoryEntityFieldStatus;

  public model: Signal<InventoryResourceModel | null> = this._inventoryResourceService.getModel();
  public tabs: Signal<ISidepanelTab[]> = this._inventoryResourceService.getTabs();
  public menuActions: Signal<MenuAction[]> = this._inventoryResourceService.getMenuActions();

  public isPageLoading: Signal<boolean> = this._inventoryResourceService.getIsPageLoading();
  public isRelationsTabLoading: Signal<boolean> = this._inventoryResourceService.getIsRelationsTabLoading();
  public isViolationsTabLoading: Signal<boolean> = this._inventoryResourceService.getIsViolationsTabLoading();
  public isHygieneTabLoading: Signal<boolean> = this._inventoryResourceService.getIsHygieneTabLoading();

  public statusLabel: Signal<StatusLabelEnum> = this._inventoryResourceService.getStatusLabel();
  public statusLabelReason: Signal<string> = this._inventoryResourceService.getStatusLabelReason();

  public agentLink: Signal<RouterNavigationParams | null> = this._inventoryResourceService.getAgentLink();
  public isAgentLinkDisabled: Signal<boolean> = this._inventoryResourceService.getIsAgentLinkDisabled();
  public agentLinkDisabledTooltip: Signal<string> = this._inventoryResourceService.getAgentLinkDisabledTooltip();

  public infoCardFields: Signal<ICardField[]> = this._inventoryResourceService.getInfoCardFields();
  public additionalInfoCardFields: Signal<ICardField[]> = this._inventoryResourceService.getAdditionalInfoCardFields();
  public environmentCardFields: Signal<ICardField[]> = this._inventoryResourceService.getEnvironmentCardFields();
  public generativeAiPropertiesCardFields: Signal<ICardField[]> =
    this._inventoryResourceService.getGenerativeAiPropertiesCardFields();
  public identitiesCardFields: Signal<ICardField[]> =
    this._inventoryResourceService.getIdentitiesCardFields();

  public isLinkDisabled: Signal<boolean> = this._inventoryResourceService.getIsLinkDisabled();

  public relatedResourcesCardsInputs: WritableSignal<RelatedResourceCardComponentInput[]> =
    this._inventoryResourceService.getRelatedResourcesCardsInputs();
  public relatedViolationsCardsInputs: Signal<RelatedViolationCardComponentInput[]> =
    this._inventoryResourceService.getRelatedViolationsCardsInputs();
  public relatedHygieneCardsInputs: Signal<RelatedViolationCardComponentInput[]> =
    this._inventoryResourceService.getRelatedHygieneCardsInputs();

  public timelineItems: Signal<ITimelineItem[]> = this._inventoryResourceService.getTimelineItems();
  public sampleRelatedResourcesDisclaimer: Signal<string> =
    this._inventoryResourceService.getSampleRelatedResourcesDisclaimer();
  public totalRelatedResources: WritableSignal<number> = this._inventoryResourceService.getTotalRelatedResources();
  public relatedResourcesSummary = computed(() => this.model()?.relatedResourcesSummary ?? {});
  private readonly _relatedResourceNavigationQueryParams: Signal<QueryParametersModel | Record<string, string>> =
    this._inventoryResourceService.getRelatedResourceNavigationQueryParams();
  public violationsPageUrl: Signal<string> = this._inventoryResourceService.getViolationsPageUrl();
  public resourceCategoryFilters: Signal<ISelectOption[]> = this._inventoryResourceService.getResourceCategoryFilters();

  public isAdditionalInfoBlockExpanded: WritableSignal<boolean> = signal<boolean>(true);
  public isActionRunningSpinner: WritableSignal<boolean> = signal<boolean>(false);
  public activeTabIndex: WritableSignal<number> = signal<number>(0);

  public errorMessage: Signal<string> = computed(() => {
    const modelValue = this.model();
    return modelValue ? populateErrorMessage(modelValue) : '';
  });

  public linkDisabledTooltip = '';
  public RefreshMethodEnum = RefreshMethod;

  private readonly _connectorCategory = ResourceCategory.connector.toString();
  private _inInventoryRoute = false;

  public ngOnInit() {
    this._inInventoryRoute = this._router.url.includes('inventory');

    this._inventoryResourceService
      .initInventoryResourceData(this.resource(), this.integration(), this.viewModel())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  public updateResourceBodyAccordionExpansionState(isExpanded: boolean) {
    this.isAdditionalInfoBlockExpanded.set(isExpanded);
  }

  public onTabSelect(index: number) {
    this.activeTabIndex.set(index);
  }

  public changeCategoriesFilters(selectedOptions: ISelectOption[]) {
    let filteredTotalRelatedResources = 0;
    const model = this.model();

    selectedOptions.forEach((includedResourceCategoryOption: ISelectOption) => {
      // The id of the option is the resource category enum which has toString()
      const includedResourceCategory =
        ResourceCategory[includedResourceCategoryOption.id as keyof typeof ResourceCategory];
      const relatedResourcesCount = model?.relatedResourcesSummary[includedResourceCategory];

      if (relatedResourcesCount) {
        filteredTotalRelatedResources += relatedResourcesCount;
      }
    });

    this.relatedResourcesCardsInputs.update((currentInputs) =>
      currentInputs.map((relatedResourcesCardsInput: RelatedResourceCardComponentInput) => {
        const selectedOption = selectedOptions.find((option) => {
          // Using value instead of id because the id is the resource category enum which has toString() that has different value then what's use for related resources cards
          return option.value === relatedResourcesCardsInput.resourceCategory;
        });
        return { ...relatedResourcesCardsInput, isVisible: selectedOption != null };
      })
    );

    this.totalRelatedResources.set(filteredTotalRelatedResources);
  }

  public actionSelected(actionString: string) {
    const action = actionString as InventorySupportedActions;
    const model = this.model() as InventoryResourceModel;

    this._inventoryActionsService
      .handleActionClicked(action, model)
      .pipe(
        tap(() => this.isActionRunningSpinner.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();

    switch (action) {
      case InventorySupportedActions.PowerplatformSetCopilotAuthentication:
        this.isActionRunningSpinner.set(true);
        break;
    }
  }

  public navigateToRelatedResources(): void {
    const navigateTo = this._inInventoryRoute ? [] : ['app/inventory'];
    const queryParams: QueryParametersModel = {
      ...this._relatedResourceNavigationQueryParams(),
    };

    const allCategoriesNum = this.resourceCategoryFilters().length;
    const selectedCategories = this.resourceCategoryFilters().filter((resource) => !resource.disabled);

    if (selectedCategories.length < allCategoriesNum && selectedCategories.length != 0) {
      queryParams['q'] += ' category:' + selectedCategories.map((resource) => resource.id).join(',');
    }

    this._router.navigate(navigateTo, { queryParams: queryParams });
  }

  public getResourceCategoryDisplayName(category: string): string {
    return getResourceCategoryDisplayName(category, this._beautifyDisplayStringPipe);
  }
}
